import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Archive, BookOpen, Download, ExternalLink, Eye, FileText, Pencil, RotateCcw, Search, Upload } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useEffectiveRole } from "../../lib/roles";
import { hasActionAccess } from "../../lib/workspaces";

type Completion = { status: "pending" | "acknowledged"; typed_signature?: string; acknowledged_at?: string; material_version?: number };
type Material = {
  id: string; title: string; description: string; category: string;
  kind: "file" | "link" | "video" | "lesson"; filename?: string | null;
  content_type?: string | null; size: number; url?: string | null; lesson_text?: string | null;
  audience_all: boolean; audience_user_ids: string[]; audience_roles: string[];
  audience_company_ids: string[]; audience_branch_ids: string[];
  due_at?: string | null; requires_acknowledgment: boolean; archived: boolean;
  version: number; uploaded_by_name: string; created_at: string; my_completion: Completion;
};
type AudienceOption = { id: string; name: string; company_id?: string | null };
type AudienceCatalog = {
  users: AudienceOption[]; companies: AudienceOption[]; branches: AudienceOption[]; roles: string[];
};
type EditState = {
  title: string; description: string; category: string; due_at: string;
  requires_acknowledgment: boolean; audience_all: boolean;
  audience_user_ids: string[]; audience_roles: string[];
  audience_company_ids: string[]; audience_branch_ids: string[];
};

const EMPTY_AUDIENCES: AudienceCatalog = { users: [], companies: [], branches: [], roles: [] };
const TRAINING_CATEGORIES = [
  "Policy",
  "User Manual",
  "Standard Operating Procedure (SOP)",
  "Operations Guideline",
  "Training Guide",
  "Onboarding",
  "Compliance & Safety",
  "Product & Service Knowledge",
  "Sales & Marketing",
  "HR & People",
  "IT & Security",
  "Template",
  "Form",
  "Other",
] as const;
const formatBytes = (size: number) =>
  size < 1024 ? `${size} B` : size < 1024 * 1024 ? `${(size / 1024).toFixed(1)} KB` : `${(size / 1024 / 1024).toFixed(1)} MB`;
const localDateTime = (value?: string | null) => value ? new Date(value).toLocaleString() : "No due date";
const toggle = (items: string[], value: string) => items.includes(value) ? items.filter((item) => item !== value) : [...items, value];

function AudiencePicker({ values, setValues, catalog }: {
  values: EditState; setValues: (next: EditState) => void; catalog: AudienceCatalog;
}) {
  return <div className="space-y-3 rounded-md border p-3 md:col-span-2" data-testid="training-audience-picker">
    <label className="flex items-center gap-2 text-sm font-medium">
      <input type="checkbox" name="audience_all" checked={values.audience_all}
        onChange={(event) => setValues({ ...values, audience_all: event.target.checked })} />
      Everyone in the tenant
    </label>
    {!values.audience_all && <div className="grid gap-3 md:grid-cols-4">
      {([
        ["Specific users", "audience_user_ids", catalog.users],
        ["Roles", "audience_roles", catalog.roles.map((role) => ({ id: role, name: role.replace(/_/g, " ") }))],
        ["Companies", "audience_company_ids", catalog.companies],
        ["Branches", "audience_branch_ids", catalog.branches],
      ] as const).map(([label, key, options]) => <fieldset key={key} className="space-y-1">
        <legend className="text-xs font-semibold uppercase text-slate-500">{label}</legend>
        <div className="max-h-32 space-y-1 overflow-auto">
          {options.map((option) => <label key={option.id} className="flex items-start gap-2 text-sm">
            <input type="checkbox" checked={values[key].includes(option.id)}
              onChange={() => setValues({ ...values, [key]: toggle(values[key], option.id) })} />
            <span>{option.name}</span>
          </label>)}
        </div>
      </fieldset>)}
    </div>}
  </div>;
}

export default function Training() {
  const { user } = useAuth();
  const role = useEffectiveRole();
  const canAdd = (role === "owner_admin" || role === "admin_support" || role === "manager_trainer") && hasActionAccess(user, "training", "add");
  const canEdit = (role === "owner_admin" || role === "admin_support" || role === "manager_trainer") && hasActionAccess(user, "training", "edit");
  const canArchive = (role === "owner_admin" || role === "admin_support" || role === "manager_trainer") && hasActionAccess(user, "training", "archive");
  const canRestore = (role === "owner_admin" || role === "admin_support" || role === "manager_trainer") && hasActionAccess(user, "training", "restore");
  const canSeeArchived = canArchive || canRestore;

  const [items, setItems] = useState<Material[]>([]);
  const [catalog, setCatalog] = useState<AudienceCatalog>(EMPTY_AUDIENCES);
  const [query, setQuery] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [kind, setKind] = useState<Material["kind"]>("file");
  const [category, setCategory] = useState<string>("Training Guide");
  const [otherCategory, setOtherCategory] = useState("");
  const [preview, setPreview] = useState<{ title: string; text: string } | null>(null);
  const [editing, setEditing] = useState<Material | null>(null);
  const [edit, setEdit] = useState<EditState | null>(null);
  const [signatureFor, setSignatureFor] = useState<Material | null>(null);
  const [signature, setSignature] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [audience, setAudience] = useState<EditState>({
    title: "", description: "", category: "General", due_at: "", requires_acknowledgment: false,
    audience_all: true, audience_user_ids: [], audience_roles: [], audience_company_ids: [], audience_branch_ids: [],
  });

  const load = async () => {
    setLoading(true);
    try {
      const response = await api.get("/tenant/training", { params: { include_archived: showArchived } });
      setItems(response.data.items || []);
    } catch {
      toast.error("Unable to load training materials.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, [showArchived]);
  useEffect(() => {
    if (!canAdd && !canEdit) return;
    api.get("/tenant/training/audiences")
      .then((response) => setCatalog(response.data || EMPTY_AUDIENCES))
      .catch(() => toast.error("Unable to load training assignment options."));
  }, [canAdd, canEdit]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((item) =>
      [item.title, item.description, item.category, item.filename || "", item.kind]
        .some((value) => value.toLowerCase().includes(needle)),
    );
  }, [items, query]);

  const fetchBlob = async (item: Material) => {
    const response = await api.get(`/tenant/training/${item.id}/file`, { responseType: "blob" });
    return response.data as Blob;
  };

  const openMaterial = async (item: Material) => {
    try {
      if (item.kind === "link" || item.kind === "video") {
        window.open(item.url || "", "_blank", "noopener,noreferrer");
      } else if (item.kind === "lesson") {
        setPreview({ title: item.title, text: item.lesson_text || "" });
      } else {
        const blob = await fetchBlob(item);
        if (item.content_type === "text/markdown" || item.content_type === "text/plain") {
          setPreview({ title: item.title, text: await blob.text() });
        } else {
          const url = URL.createObjectURL(blob);
          window.open(url, "_blank", "noopener,noreferrer");
          window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
        }
      }
    } catch {
      toast.error("Unable to open this training material.");
    }
  };

  const downloadMaterial = async (item: Material) => {
    try {
      const blob = await fetchBlob(item);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url; anchor.download = item.filename || "training-file"; anchor.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Unable to download this training file.");
    }
  };

  const createMaterial = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    data.set("kind", kind);
    data.set("category", category === "Other" ? otherCategory.trim() : category);
    data.set("audience_all", String(audience.audience_all));
    for (const key of ["audience_user_ids", "audience_roles", "audience_company_ids", "audience_branch_ids"] as const) {
      data.set(key, JSON.stringify(audience[key]));
    }
    data.set("requires_acknowledgment", String(audience.requires_acknowledgment));
    setUploading(true);
    try {
      await api.post("/tenant/training", data);
      form.reset(); setKind("file"); setCategory("Training Guide"); setOtherCategory("");
      setAudience({ ...audience, audience_all: true, audience_user_ids: [], audience_roles: [], audience_company_ids: [], audience_branch_ids: [], requires_acknowledgment: false });
      toast.success("Training material published and assigned.");
      await load();
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      const messages: Record<string, string> = {
        training_file_too_large: "File must be 5 MB or smaller.",
        training_url_invalid: "Enter a complete http:// or https:// address.",
        training_audience_required: "Choose at least one audience.",
      };
      toast.error(messages[detail] || "Unable to publish training material.");
    } finally {
      setUploading(false);
    }
  };

  const beginEdit = (item: Material) => {
    setEditing(item);
    setEdit({
      title: item.title, description: item.description, category: item.category,
      due_at: item.due_at ? item.due_at.slice(0, 16) : "",
      requires_acknowledgment: item.requires_acknowledgment,
      audience_all: item.audience_all,
      audience_user_ids: item.audience_user_ids || [], audience_roles: item.audience_roles || [],
      audience_company_ids: item.audience_company_ids || [], audience_branch_ids: item.audience_branch_ids || [],
    });
  };

  const saveEdit = async () => {
    if (!editing || !edit || !edit.title.trim()) return;
    try {
      await api.patch(`/tenant/training/${editing.id}`, { ...edit, due_at: edit.due_at || null, expected_version: editing.version });
      toast.success("Training material updated. A new acknowledgment is required for the new version.");
      setEditing(null); setEdit(null); await load();
    } catch (error: any) {
      toast.error(error?.response?.data?.detail === "training_version_conflict" ? "This material changed. Refresh and try again." : "Unable to update training material.");
    }
  };

  const archive = async (item: Material) => {
    if (!window.confirm(`Archive “${item.title}”? Completion evidence will be preserved.`)) return;
    try {
      await api.post(`/tenant/training/${item.id}/archive`);
      toast.success("Training material archived."); await load();
    } catch { toast.error("Unable to archive training material."); }
  };

  const restore = async (item: Material) => {
    try {
      await api.post(`/tenant/training/${item.id}/restore`);
      toast.success("Training material restored."); await load();
    } catch { toast.error("Unable to restore training material."); }
  };

  const acknowledge = async () => {
    if (!signatureFor || !confirmed || signature.trim().length < 2) return;
    try {
      await api.post(`/tenant/training/${signatureFor.id}/acknowledge`, {
        typed_signature: signature.trim(), acknowledgment_confirmed: true, expected_version: signatureFor.version,
      });
      toast.success("Acknowledgment and signature recorded.");
      setSignatureFor(null); setSignature(""); setConfirmed(false); await load();
    } catch (error: any) {
      toast.error(error?.response?.data?.detail === "training_version_conflict" ? "A new version is available. Reopen it before acknowledging." : "Unable to record acknowledgment.");
    }
  };

  return <div className="mx-auto max-w-6xl p-4 md:p-6" data-testid="training-workspace">
    <header className="mb-5">
      <div className="flex items-center gap-2"><BookOpen className="h-6 w-6" /><h1 className="text-2xl font-semibold">Training</h1></div>
      <p className="mt-1 text-sm text-slate-500">Assigned lessons, policies, files, videos, and reference links with completion evidence.</p>
    </header>

    {canAdd && <form onSubmit={createMaterial} className="mb-6 grid gap-3 rounded-lg border bg-white p-4 shadow-sm md:grid-cols-2" data-testid="training-create-form">
      <div className="md:col-span-2"><h2 className="font-medium">Publish training material</h2><p className="text-xs text-slate-500">Policies always require signed acknowledgment. File limit: 5 MB.</p></div>
      <input required name="title" maxLength={160} placeholder="Title" className="rounded-md border px-3 py-2" />
      <div>
        <label htmlFor="training-category" className="mb-1 block text-sm font-medium">Category</label>
        <select id="training-category" value={category} onChange={(event) => setCategory(event.target.value)} className="w-full rounded-md border px-3 py-2" data-testid="training-category">
          {TRAINING_CATEGORIES.map((value) => <option key={value} value={value}>{value}</option>)}
        </select>
      </div>
      <select value={kind} onChange={(event) => setKind(event.target.value as Material["kind"])} className="rounded-md border px-3 py-2" data-testid="training-kind">
        <option value="file">PDF or document</option><option value="video">Video link</option><option value="link">Website or resource link</option><option value="lesson">Written lesson</option>
      </select>
      <input name="due_at" type="datetime-local" className="rounded-md border px-3 py-2" />
      {category === "Other" && <input required value={otherCategory} onChange={(event) => setOtherCategory(event.target.value)} maxLength={80} placeholder="Enter the custom category" className="rounded-md border px-3 py-2 md:col-span-2" data-testid="training-other-category" />}
      <textarea name="description" maxLength={1000} placeholder="Short description" className="rounded-md border px-3 py-2 md:col-span-2" />
      {kind === "file" && <input required name="file" type="file" accept=".md,.markdown,.txt,.pdf,text/markdown,text/plain,application/pdf" className="rounded-md border px-3 py-2 md:col-span-2" />}
      {(kind === "link" || kind === "video") && <input required name="url" type="url" placeholder={kind === "video" ? "https://… video address" : "https://… resource address"} className="rounded-md border px-3 py-2 md:col-span-2" />}
      {kind === "lesson" && <textarea required name="lesson_text" maxLength={100000} rows={8} placeholder="Write the complete lesson…" className="rounded-md border px-3 py-2 md:col-span-2" />}
      <label className="flex items-center gap-2 text-sm md:col-span-2"><input type="checkbox" checked={audience.requires_acknowledgment} onChange={(event) => setAudience({ ...audience, requires_acknowledgment: event.target.checked })} /> Require completion acknowledgment and typed signature</label>
      <AudiencePicker values={audience} setValues={setAudience} catalog={catalog} />
      <button disabled={uploading} className="flex min-h-11 items-center justify-center gap-2 rounded-md bg-slate-900 px-4 text-sm font-medium text-white disabled:opacity-50 md:w-fit"><Upload className="h-4 w-4" /> {uploading ? "Publishing…" : "Publish & assign"}</button>
    </form>}

    <div className="mb-4 flex flex-wrap gap-3">
      <div className="flex flex-1 items-center gap-2 rounded-md border bg-white px-3"><Search className="h-4 w-4 text-slate-400" /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search training materials" className="min-h-11 flex-1 bg-transparent outline-none" /></div>
      {canSeeArchived && <label className="flex items-center gap-2 rounded-md border bg-white px-3 text-sm"><input type="checkbox" checked={showArchived} onChange={(event) => setShowArchived(event.target.checked)} /> Show archived</label>}
    </div>

    {loading ? <p className="text-sm text-slate-500">Loading training materials…</p> : filtered.length === 0 ? <div className="rounded-lg border border-dashed p-8 text-center text-sm text-slate-500"><FileText className="mx-auto mb-2 h-8 w-8" />{items.length ? "No training materials match your search." : "No training materials are assigned to you yet."}</div> :
      <div className="grid gap-3">{filtered.map((item) => <article key={item.id} className={`rounded-lg border bg-white p-4 shadow-sm ${item.archived ? "opacity-65" : ""}`} data-testid={`training-material-${item.id}`}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 text-xs font-medium uppercase tracking-wide text-slate-500"><span>{item.category}</span><span>· {item.kind}</span>{item.archived && <span className="rounded bg-slate-200 px-2 py-0.5">Archived</span>}</div>
            <h2 className="font-semibold">{item.title}</h2>
            {item.description && <p className="mt-1 text-sm text-slate-600">{item.description}</p>}
            <p className="mt-2 text-xs text-slate-500">Due: {localDateTime(item.due_at)} · Version {item.version} · by {item.uploaded_by_name}</p>
            {item.kind === "file" && <p className="text-xs text-slate-400">{item.filename} · {formatBytes(item.size)}</p>}
            {item.requires_acknowledgment && <p className={`mt-2 text-sm font-medium ${item.my_completion.status === "acknowledged" ? "text-emerald-700" : "text-amber-700"}`} data-testid={`training-status-${item.id}`}>
              {item.my_completion.status === "acknowledged" ? `Acknowledged ${localDateTime(item.my_completion.acknowledged_at)}` : "Acknowledgment required"}
            </p>}
          </div>
          <div className="flex flex-wrap gap-2">
            {!item.archived && <button type="button" onClick={() => void openMaterial(item)} className="flex min-h-10 items-center gap-1 rounded-md border px-3 text-sm">{item.kind === "link" || item.kind === "video" ? <ExternalLink className="h-4 w-4" /> : <Eye className="h-4 w-4" />} Open</button>}
            {!item.archived && item.kind === "file" && <button type="button" onClick={() => void downloadMaterial(item)} className="flex min-h-10 items-center gap-1 rounded-md border px-3 text-sm"><Download className="h-4 w-4" /> Download</button>}
            {!item.archived && item.requires_acknowledgment && item.my_completion.status !== "acknowledged" && <button type="button" onClick={() => setSignatureFor(item)} className="min-h-10 rounded-md bg-emerald-700 px-3 text-sm font-medium text-white" data-testid={`training-acknowledge-${item.id}`}>Acknowledge & sign</button>}
            {!item.archived && canEdit && <button type="button" onClick={() => beginEdit(item)} className="flex min-h-10 items-center gap-1 rounded-md border px-3 text-sm"><Pencil className="h-4 w-4" /> Edit</button>}
            {!item.archived && canArchive && <button type="button" onClick={() => void archive(item)} className="flex min-h-10 items-center gap-1 rounded-md border border-amber-200 px-3 text-sm text-amber-800"><Archive className="h-4 w-4" /> Archive</button>}
            {item.archived && canRestore && <button type="button" onClick={() => void restore(item)} className="flex min-h-10 items-center gap-1 rounded-md border px-3 text-sm"><RotateCcw className="h-4 w-4" /> Restore</button>}
          </div>
        </div>
      </article>)}</div>}

    {preview && <div className="fixed inset-0 z-[80] grid place-items-center bg-slate-950/50 p-3" role="dialog" aria-modal="true" aria-label={preview.title}><div className="flex max-h-[90dvh] w-full max-w-4xl flex-col rounded-lg bg-white shadow-xl"><div className="flex items-center justify-between border-b p-4"><h2 className="font-semibold">{preview.title}</h2><button onClick={() => setPreview(null)} className="min-h-10 rounded-md border px-3 text-sm">Close</button></div><div className="overflow-auto whitespace-pre-wrap p-4 text-sm leading-6">{preview.text}</div></div></div>}

    {signatureFor && <div className="fixed inset-0 z-[90] grid place-items-center bg-slate-950/50 p-3" role="dialog" aria-modal="true" aria-label="Training acknowledgment"><div className="w-full max-w-lg space-y-4 rounded-lg bg-white p-5 shadow-xl">
      <h2 className="font-semibold">Acknowledge “{signatureFor.title}”</h2>
      <p className="text-sm text-slate-600">Your signature confirms that you received and reviewed version {signatureFor.version}. It does not mean that you agree with every statement.</p>
      <input value={signature} onChange={(event) => setSignature(event.target.value)} placeholder="Type your full legal name" className="w-full rounded-md border px-3 py-2" data-testid="training-signature" />
      <label className="flex items-start gap-2 text-sm"><input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} /> I confirm that I received and reviewed this training material.</label>
      <div className="flex justify-end gap-2"><button type="button" onClick={() => { setSignatureFor(null); setSignature(""); setConfirmed(false); }} className="rounded-md border px-4 py-2 text-sm">Cancel</button><button type="button" onClick={() => void acknowledge()} disabled={!confirmed || signature.trim().length < 2} className="rounded-md bg-slate-900 px-4 py-2 text-sm text-white disabled:opacity-50" data-testid="training-sign-submit">Record acknowledgment</button></div>
    </div></div>}

    {editing && edit && <div className="fixed inset-0 z-[90] grid place-items-center bg-slate-950/50 p-3" role="dialog" aria-modal="true" aria-label="Edit training material"><div className="max-h-[90dvh] w-full max-w-3xl space-y-3 overflow-auto rounded-lg bg-white p-5 shadow-xl">
      <h2 className="font-semibold">Edit training material</h2>
      <div className="grid gap-3 md:grid-cols-2">
        <input value={edit.title} onChange={(event) => setEdit({ ...edit, title: event.target.value })} placeholder="Title" className="rounded-md border px-3 py-2" />
        <div>
          <label htmlFor="training-edit-category" className="mb-1 block text-sm font-medium">Category</label>
          <select id="training-edit-category"
            value={TRAINING_CATEGORIES.includes(edit.category as typeof TRAINING_CATEGORIES[number]) ? edit.category : "Other"}
            onChange={(event) => setEdit({ ...edit, category: event.target.value })}
            className="w-full rounded-md border px-3 py-2">
            {TRAINING_CATEGORIES.map((value) => <option key={value} value={value}>{value}</option>)}
          </select>
        </div>
        {(edit.category === "Other" || !TRAINING_CATEGORIES.includes(edit.category as typeof TRAINING_CATEGORIES[number])) && <input value={edit.category === "Other" ? "" : edit.category} onChange={(event) => setEdit({ ...edit, category: event.target.value || "Other" })} maxLength={80} placeholder="Enter the custom category" className="rounded-md border px-3 py-2" data-testid="training-edit-other-category" />}
        <textarea value={edit.description} onChange={(event) => setEdit({ ...edit, description: event.target.value })} placeholder="Description" className="rounded-md border px-3 py-2 md:col-span-2" />
        <input type="datetime-local" value={edit.due_at} onChange={(event) => setEdit({ ...edit, due_at: event.target.value })} className="rounded-md border px-3 py-2" />
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={edit.requires_acknowledgment} onChange={(event) => setEdit({ ...edit, requires_acknowledgment: event.target.checked })} /> Require acknowledgment</label>
        <AudiencePicker values={edit} setValues={setEdit} catalog={catalog} />
      </div>
      <p className="text-xs text-amber-700">Saving creates a new material version. Previous acknowledgment evidence remains preserved and users must acknowledge the new version.</p>
      <div className="flex justify-end gap-2"><button type="button" onClick={() => { setEditing(null); setEdit(null); }} className="rounded-md border px-4 py-2 text-sm">Cancel</button><button type="button" onClick={() => void saveEdit()} className="rounded-md bg-slate-900 px-4 py-2 text-sm text-white">Save new version</button></div>
    </div></div>}
  </div>;
}
